import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bluetooth, Download, ChefHat } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, useApi, errMsg } from "@/pos/lib/api";
import { useAuth } from "@/pos/context/AuthContext";
import { Page, PageHeader } from "@/pos/components/Common";
import Field from "@/pos/components/Field";
import { getPrinter, setPrinter, printDocs, testDoc, getKitchenMode, setKitchenMode, getRole, setRole } from "@/pos/lib/printer";
import { resetShopCache, loadKitchenMode } from "@/pos/lib/print";
import { bleSupported, bleStatus, bleConnect, bleReconnect, bleDisconnect, onBle, kitchenBle } from "@/pos/lib/ble";

function Choice({ value, options, onChange, testid }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid={testid}>
      {options.map(([v, l]) => (
        <button key={v} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
          className={`rounded-xl border-2 px-3 py-2.5 text-sm font-semibold transition-colors ${value === v ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200 text-slate-600"}`}>{l}</button>
      ))}
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-4 rounded-xl border border-slate-100 p-3">
      <div><div className="text-sm font-semibold">{label}</div><div className="text-xs text-slate-500">{hint}</div></div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}

function DirectCard() {
  const [st, setSt] = useState(bleStatus());
  const [busy, setBusy] = useState(false);
  useEffect(() => { bleReconnect(); return onBle(setSt); }, []);
  const connect = async () => {
    setBusy(true);
    try { const s = await bleConnect(); toast.success(`Printer ${s.name || ""} disambung`); }
    catch (e) { if (e?.name !== "NotFoundError") toast.error(e?.message || "Gagal sambung printer"); }
    finally { setBusy(false); }
  };
  if (!bleSupported()) return <div className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="ble-unsupported">Pelayar ini tidak menyokong Bluetooth terus. Buka POS ini dalam <b>Google Chrome</b> di tablet Android.</div>;
  return (
    <div className="space-y-3 rounded-xl border border-red-100 p-4" data-testid="ble-card">
      <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-700">
        <li>Hidupkan printer dan Bluetooth + Lokasi tablet. <b>Jangan</b> buka printer dalam app RawBT (tutup RawBT).</li>
        <li>Buka POS ini dalam <b>Google Chrome</b>, tekan <b>Sambung Printer</b> dan pilih nama printer anda.</li>
        <li>Tekan <b>Cetak Ujian</b>. Jika printer dimatikan atau halaman dimuat semula, tekan Sambung Printer sekali lagi.</li>
      </ol>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={connect} disabled={busy} className="btn-primary" data-testid="ble-connect-btn"><Bluetooth size={16} /> {busy ? "Menyambung..." : st.connected ? "Tukar Printer" : "Sambung Printer"}</button>
        {st.connected && <button onClick={bleDisconnect} className="btn-outline" data-testid="ble-disconnect-btn">Putuskan</button>}
        <span className={`text-sm font-semibold ${st.connected ? "text-green-700" : "text-slate-500"}`} data-testid="ble-status">{st.connected ? `Bersambung: ${st.name || "Printer"}` : "Belum bersambung"}</span>
      </div>
      <p className="text-xs text-slate-500">Hanya printer yang menyokong Bluetooth Low Energy (BLE) akan muncul. Jika printer anda tiada dalam senarai, guna pilihan RawBT.</p>
    </div>
  );
}

function RawbtSteps() {
  return (
    <>
      <ol className="list-decimal space-y-1 rounded-xl bg-red-50/70 p-4 pl-8 text-sm text-slate-700" data-testid="printer-steps">
        <li>Hidupkan printer, kemudian pair di tablet: <b>Tetapan Android → Bluetooth</b>.</li>
        <li>Pasang app percuma <b>RawBT</b> dari Play Store, pilih printer anda di RawBT, dan tekan "Test".</li>
        <li>Pilihan (untuk auto-cetak tanpa sentuh): pasang <b>"Server for RawBT"</b> dan tekan <b>Start</b>. <i>Jika Play Store kata app ini tidak serasi dengan tablet anda, guna pilihan <b>Bluetooth terus</b> di atas.</i> Tanpa app ini, resit masih boleh dicetak tetapi RawBT akan dibuka setiap kali.</li>
        <li>Tekan <b>Cetak Ujian</b> di bawah. Biarkan halaman POS ini terbuka di tablet kaunter.</li>
      </ol>
      <div className="flex flex-wrap gap-2">
        <a href="https://play.google.com/store/apps/details?id=ru.a402d.rawbtprinter" target="_blank" rel="noreferrer" className="btn-outline" data-testid="rawbt-download-link"><Download size={16} /> Muat turun RawBT</a>
        <a href="https://play.google.com/store/apps/details?id=rawbt.server" target="_blank" rel="noreferrer" className="btn-outline" data-testid="rawbt-server-download-link"><Download size={16} /> Muat turun Server for RawBT</a>
      </div>
    </>
  );
}

function PrinterCard() {
  const [c, setC] = useState(getPrinter());
  const upd = (patch) => { const n = { ...c, ...patch }; setC(n); setPrinter(n); };
  const test = async () => { if ((await printDocs([testDoc(c.width)])) === "failed") toast.error("Printer belum disambung. Tekan Sambung Printer dahulu."); };
  return (
    <div className="card-surface max-w-2xl space-y-4 p-6" data-testid="printer-card">
      <div className="flex items-center gap-2"><Bluetooth className="text-red-600" /><h2 className="font-heading text-lg font-bold">Printer Bluetooth (tablet ini)</h2></div>
      <Field label="Cara cetak"><Choice value={c.method} onChange={(v) => upd({ method: v })} testid="printer-method"
        options={[["direct", "Bluetooth terus (tanpa app)"], ["rawbt", "Bluetooth (RawBT)"], ["browser", "Dialog cetak pelayar"]]} /></Field>
      {c.method === "direct" && <DirectCard />}
      {c.method === "rawbt" && <RawbtSteps />}
      <Field label="Saiz kertas"><Choice value={c.width} onChange={(v) => upd({ width: v })} testid="printer-width"
        options={[[32, "58 mm"], [48, "80 mm"]]} /></Field>
      <Toggle label="Auto cetak pesanan app" hint="Resit keluar sendiri bila pelanggan order dari app (hidupkan di SATU tablet sahaja)" checked={c.autoOnline} onChange={(v) => upd({ autoOnline: v })} testid="printer-auto-online" />
      <Toggle label="Auto cetak selepas bayar" hint="Resit keluar terus selepas bayaran di kaunter" checked={c.autoPaid} onChange={(v) => upd({ autoPaid: v })} testid="printer-auto-paid" />
      <button onClick={test} className="btn-primary" data-testid="printer-test-btn"><Bluetooth size={16} /> Cetak Ujian</button>
    </div>
  );
}

function KitchenBleCard() {
  const [st, setSt] = useState(kitchenBle.status());
  const [busy, setBusy] = useState(false);
  useEffect(() => { kitchenBle.reconnect(); return kitchenBle.on(setSt); }, []);
  const connect = async () => {
    setBusy(true);
    try {
      const s = await kitchenBle.connect();
      if (s.id && s.id === bleStatus().id) { kitchenBle.disconnect(); toast.error("Printer ini sudah digunakan sebagai printer kaunter. Pilih printer lain."); return; }
      toast.success(`Printer dapur ${s.name || ""} disambung`);
    } catch (e) { if (e?.name !== "NotFoundError") toast.error(e?.message || "Gagal sambung printer dapur"); } finally { setBusy(false); }
  };
  const test = async () => { if ((await printDocs([testDoc(getPrinter().width)], { kitchen: true })) === "failed") toast.error("Printer dapur belum disambung."); };
  if (!bleSupported()) return <div className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="kitchen-ble-unsupported">Printer dapur kedua perlukan Bluetooth terus. Buka POS dalam <b>Google Chrome</b> di tablet Android.</div>;
  return (
    <div className="space-y-3 rounded-xl border border-amber-100 bg-amber-50/40 p-4" data-testid="kitchen-ble-card">
      <p className="text-sm text-slate-700">Sambung printer <b>kedua</b> (printer dapur). Tiket dapur keluar di sini, resit pelanggan keluar di printer kaunter. Jika printer dapur terputus, tiket dicetak di printer kaunter.</p>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={connect} disabled={busy} className="btn-primary" data-testid="kitchen-ble-connect-btn"><Bluetooth size={16} /> {busy ? "Menyambung..." : st.connected ? "Tukar Printer Dapur" : "Sambung Printer Dapur"}</button>
        {st.connected && <button onClick={kitchenBle.disconnect} className="btn-outline" data-testid="kitchen-ble-disconnect-btn">Putuskan</button>}
        <button onClick={test} className="btn-outline" data-testid="kitchen-ble-test-btn">Cetak Ujian Dapur</button>
        <span className={`text-sm font-semibold ${st.connected ? "text-green-700" : "text-slate-500"}`} data-testid="kitchen-ble-status">{st.connected ? `Bersambung: ${st.name || "Printer"}` : "Belum bersambung"}</span>
      </div>
    </div>
  );
}

function KitchenCard() {
  const { isOwner } = useAuth();
  const [mode, setMode] = useState(getKitchenMode());
  const [role, setR] = useState(getRole());
  useEffect(() => { loadKitchenMode().then(setMode); }, []);
  const pickMode = async (m) => {
    try { await api.put("/print-config", { kitchen_mode: m }); setKitchenMode(m); setMode(m); toast.success("Mod printer dapur disimpan untuk semua tablet"); } catch (e) { toast.error(errMsg(e)); }
  };
  const pickRole = (r) => { setRole(r); setR(r); };
  return (
    <div className="card-surface max-w-2xl space-y-4 p-6" data-testid="kitchen-printer-card">
      <div className="flex items-center gap-2"><ChefHat className="text-amber-600" /><h2 className="font-heading text-lg font-bold">Printer Dapur</h2></div>
      <p className="text-sm text-slate-500">Tiket dapur hanya ada nama menu, kuantiti, add-on dan nota (tanpa harga).{!isOwner && " Hanya pemilik boleh tukar mod."}</p>
      <Field label="Susunan printer dapur (semua tablet)">
        <div className="grid gap-2 sm:grid-cols-3" data-testid="kitchen-mode">
          {[["off", "Tiada tiket dapur"], ["same", "1 tablet, 2 printer"], ["tablet", "2 tablet (kaunter + dapur)"]].map(([v, l]) => (
            <button key={v} disabled={!isOwner} onClick={() => pickMode(v)} data-testid={`kitchen-mode-${v}`}
              className={`rounded-xl border-2 px-3 py-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed ${mode === v ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200 text-slate-600"}`}>{l}</button>
          ))}
        </div>
      </Field>
      {mode === "same" && <KitchenBleCard />}
      {mode === "tablet" && (
        <>
          <Field label="Tablet ini digunakan di"><Choice value={role} onChange={pickRole} testid="tablet-role" options={[["kaunter", "Kaunter (resit)"], ["dapur", "Dapur (tiket dapur)"]]} /></Field>
          <div className="rounded-xl bg-red-50/70 p-3 text-sm text-slate-700" data-testid="tablet-role-hint">
            {role === "dapur"
              ? <>Tablet ini akan <b>cetak tiket dapur sendiri</b> bila ada order baru (dari kaunter atau app). Sambungkan printer dapur pada bahagian <b>Printer Bluetooth</b> di atas dan biarkan POS terbuka.</>
              : <>Tablet ini hanya cetak resit pelanggan. Tiket dapur akan dicetak oleh tablet dapur.</>}
          </div>
        </>
      )}
    </div>
  );
}

function ShopCard() {
  const { data } = useApi("/settings");
  const [f, setF] = useState(null);
  useEffect(() => { if (data) setF(data); }, [data]);
  if (!f) return <div className="text-slate-500">Memuatkan...</div>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try { await api.put("/settings", f); resetShopCache(); toast.success("Tetapan disimpan"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-surface max-w-2xl space-y-4 p-6">
      <h2 className="font-heading text-lg font-bold">Maklumat pada resit</h2>
      <Field label="Nama Kedai"><Input value={f.shop_name} onChange={set("shop_name")} data-testid="settings-name-input" /></Field>
      <Field label="Alamat"><Textarea value={f.address} onChange={set("address")} data-testid="settings-address-input" /></Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="No. Telefon"><Input value={f.phone} onChange={set("phone")} data-testid="settings-phone-input" /></Field>
        <Field label="No. SSM"><Input value={f.ssm} onChange={set("ssm")} data-testid="settings-ssm-input" /></Field>
      </div>
      <Field label="Nota Kaki Resit"><Input value={f.footer} onChange={set("footer")} data-testid="settings-footer-input" /></Field>
      <button onClick={save} className="btn-primary" data-testid="settings-save-btn">Simpan Tetapan</button>
    </div>
  );
}

export default function Settings() {
  const { isOwner } = useAuth();
  return (
    <Page>
      <PageHeader title="Tetapan & Printer" subtitle="Resit, printer Bluetooth" />
      <div className="space-y-6">
        <PrinterCard />
        <KitchenCard />
        {isOwner && <ShopCard />}
      </div>
    </Page>
  );
}
